import React, { useState, useEffect } from 'react';
import { Search, Filter, Heart, Clock, BarChart3, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardFooter, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Activities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAge, setSelectedAge] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [favorites, setFavorites] = useState([]);
  const [savingFavorite, setSavingFavorite] = useState(null);
  const { isAuthenticated, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchActivities();
    if (isAuthenticated) {
      fetchUserFavorites();
    }
  }, [isAuthenticated]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/activities`);
      setActivities(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError('Failed to load activities. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserFavorites = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data.map(activity => activity.id));
    } catch (err) {
      console.error('Error fetching favorites:', err);
    }
  };

  const toggleFavorite = async (id) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setSavingFavorite(id);
    try {
      const isFavorited = favorites.includes(id);
      
      if (isFavorited) {
        await axios.delete(`${API_URL}/api/user/favorites/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(prev => prev.filter(fav => fav !== id));
      } else {
        await axios.post(`${API_URL}/api/user/favorites/${id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(prev => [...prev, id]);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    } finally {
      setSavingFavorite(null);
    }
  };

  const ageGroups = [
    { value: 'all', label: 'All Ages' },
    { value: 'newborn-2', label: 'Newborn-2 Years' },
    { value: '3-5', label: '3-5 Years' },
    { value: '6-10', label: '6-10 Years' },
    { value: '11-13', label: '11-13 Years' },
    { value: '14-18', label: '14-18 Years' }
  ];

  const categories = [
    'all',
    'Physical Development',
    'Cognitive Development',
    'Nature & Science',
    'Life Skills',
    'Building & Making',
    'Technology & STEM',
    'Arts & Creativity',
    'Financial Literacy',
    'Automotive Skills'
  ];

  const filteredActivities = activities.filter(activity => {
    const matchesSearch = activity.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          activity.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesAge = selectedAge === 'all' || activity.ageGroup === selectedAge;
    const matchesCategory = selectedCategory === 'all' || activity.category === selectedCategory;
    return matchesSearch && matchesAge && matchesCategory;
  });

  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'Easy': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-green-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-black mb-4">
            <span className="bg-gradient-to-r from-blue-600 via-teal-600 to-green-600 bg-clip-text text-transparent">
              Activity Library
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto font-medium">
            Browse hundreds of fun, educational activities to build connection and teach cool skills!
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-xl border-4 border-teal-200 p-6 mb-8 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-teal-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Search for fun activities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 border-2 border-teal-300 focus:border-teal-500"
              />
            </div>
            
            {/* Age Filter */}
            <select
              value={selectedAge}
              onChange={(e) => setSelectedAge(e.target.value)}
              className="h-12 px-4 rounded-md border-2 border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500 font-medium"
            >
              {ageGroups.map(group => (
                <option key={group.value} value={group.value}>
                  {group.label}
                </option>
              ))}
            </select>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="h-12 px-4 rounded-md border-2 border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500 font-medium"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? 'All Categories' : cat}
                </option>
              ))}
            </select>
          </div>

          <div className="text-sm text-gray-600">
            Showing {filteredActivities.length} {filteredActivities.length === 1 ? 'activity' : 'activities'}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-12 w-12 animate-spin text-teal-500" />
            <span className="ml-3 text-xl text-gray-600">Loading activities...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-20">
            <div className="bg-red-50 text-red-600 p-6 rounded-xl inline-block">
              <p className="text-lg font-medium">{error}</p>
              <Button onClick={fetchActivities} className="mt-4 bg-red-500 hover:bg-red-600 text-white">
                Try Again
              </Button>
            </div>
          </div>
        )}

        {/* Activities Grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredActivities.map((activity) => (
            <Card key={activity.id} className="overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 hover:rotate-1 border-4 border-teal-200 hover:border-teal-400 group bg-gradient-to-br from-white to-teal-50">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={activity.imageUrl} 
                  alt={activity.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <button
                  onClick={() => toggleFavorite(activity.id)}
                  disabled={savingFavorite === activity.id}
                  className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-lg hover:bg-teal-50 transition-colors border-2 border-teal-300 disabled:opacity-50"
                >
                  {savingFavorite === activity.id ? (
                    <Loader2 className="h-5 w-5 animate-spin text-teal-500" />
                  ) : (
                    <Heart 
                      className={`h-5 w-5 ${favorites.includes(activity.id) ? 'fill-red-500 text-red-500' : 'text-gray-600'}`}
                    />
                  )}
                </button>
              </div>
              
              <CardHeader>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-gradient-to-r from-blue-400 to-teal-400 text-white font-bold">
                      {activity.category}
                    </Badge>
                    <Badge className={getDifficultyColor(activity.difficulty)}>
                      {activity.difficulty}
                    </Badge>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                    {activity.title}
                  </h3>
                </div>
              </CardHeader>
              
              <CardContent>
                <p className="text-gray-600 mb-4">{activity.description}</p>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>{activity.duration}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <BarChart3 className="h-4 w-4" />
                    <span>{activity.ageGroup}</span>
                  </div>
                </div>
              </CardContent>
              
              <CardFooter>
                <Link to={`/activity/${activity.id}`} className="w-full">
                  <Button className="w-full bg-gradient-to-r from-blue-500 via-teal-500 to-green-500 hover:from-blue-600 hover:via-teal-600 hover:to-green-600 text-white font-bold shadow-lg">
                    View Details
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          ))}
          </div>
        )}

        {!loading && !error && filteredActivities.length === 0 && (
          <div className="text-center py-20">
            <Filter className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-700 mb-2">No activities found</h3>
            <p className="text-gray-600">Try adjusting your filters or search query</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Activities;